import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Stage, Layer } from 'react-konva'
import FloatingToolbar from './FloatingToolbar'
import TransformableText from './TransformableText'
import TransformableImage from './TransformableImage'
import useHistory from './useHistory'
import { getCanvasDimensions, isImageField, createTextField } from './canvasUtils'
import styles from './KonvaCanvas.module.scss'

export default function KonvaCanvas({
  fields,
  setFields,
  imageUrl,
  template,
  onSelectField,
  fonts = [],
  maxContainerWidth = null,
  hideBoundingBoxes: hideBoundingBoxesProp = null,
  onHideBoundingBoxesChange = null,
  showControls = true,
}) {
  // Selection and editing state
  const [selectedId, setSelectedId] = useState(null)
  const [editingField, setEditingField] = useState(null)
  const [editText, setEditText] = useState('')
  const [hideBoundingBoxesLocal, setHideBoundingBoxesLocal] = useState(false)

  // Allow controlled or uncontrolled mode for hideBoundingBoxes
  const hideBoundingBoxes = hideBoundingBoxesProp !== null ? hideBoundingBoxesProp : hideBoundingBoxesLocal
  const setHideBoundingBoxes = onHideBoundingBoxesChange || setHideBoundingBoxesLocal

  // Toolbar and clipboard state
  const [toolbarPosition, setToolbarPosition] = useState({ x: 100, y: 100 })
  const [copiedField, setCopiedField] = useState(null)
  const [copiedFormatting, setCopiedFormatting] = useState(null)
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, fieldId: null, pastePosition: null })
  const [displayScale, setDisplayScale] = useState(1)

  // Toast notification state
  const [toast, setToast] = useState({ visible: false, message: '' })

  // Refs
  const stageRef = useRef()
  const canvasWrapperRef = useRef()
  const containerRef = useRef()
  const textInputRef = useRef()
  const editTextRef = useRef('')
  const positionedForSelectionRef = useRef(null)

  // Calculate canvas dimensions from template
  const dimensions = getCanvasDimensions(
    template.scaling_factor,
    template.image_width,
    template.image_height
  )

  // Toast notification helper
  const showToast = useCallback((message, duration = 2000) => {
    setToast({ visible: true, message })
    setTimeout(() => setToast({ visible: false, message: '' }), duration)
  }, [])

  // Undo/Redo using custom hook
  const { handleUndo, handleRedo } = useHistory(fields, setFields, showToast)

  // Keep editText ref in sync
  useEffect(() => {
    editTextRef.current = editText
  }, [editText])

  // Responsive scaling
  useEffect(() => {
    let rafId = null

    const calculateScale = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        if (!containerRef.current || dimensions.width <= 0) return

        const parent = containerRef.current.parentElement
        const availableWidth = maxContainerWidth || (parent ? parent.clientWidth - 32 : window.innerWidth - 64)

        if (availableWidth > 0 && dimensions.width > availableWidth) {
          const newScale = availableWidth / dimensions.width
          setDisplayScale(prev => Math.abs(prev - newScale) > 0.01 ? newScale : prev)
        } else if (availableWidth > 0) {
          setDisplayScale(prev => prev !== 1 ? 1 : prev)
        }
      })
    }

    const initialTimeout = setTimeout(calculateScale, 100)
    window.addEventListener('resize', calculateScale)

    return () => {
      clearTimeout(initialTimeout)
      if (rafId) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', calculateScale)
    }
  }, [dimensions.width, maxContainerWidth])

  // Get selected field
  const selectedField = selectedId ? fields[selectedId] : null

  // Update toolbar position when selection changes
  useEffect(() => {
    if (selectedField && canvasWrapperRef.current) {
      const fieldX = selectedField.left || 0
      const fieldY = selectedField.top || 0
      const fieldHeight = selectedField.height || 30

      const positionKey = `${selectedId}-${Math.round(fieldX)}-${Math.round(fieldY)}-${Math.round(fieldHeight)}`
      if (positionKey !== positionedForSelectionRef.current) {
        positionedForSelectionRef.current = positionKey
        const wrapperRect = canvasWrapperRef.current.getBoundingClientRect()
        setToolbarPosition({
          x: wrapperRect.left + (fieldX * displayScale),
          y: wrapperRect.top + ((fieldY + fieldHeight) * displayScale) + 10
        })
      }
    } else if (!selectedId) {
      positionedForSelectionRef.current = null
    }
  }, [selectedId, selectedField?.left, selectedField?.top, selectedField?.height, displayScale])

  // Handle field change (drag/resize)
  const handleFieldChange = useCallback((updatedField) => {
    const fieldObj = updatedField.object
    if (fieldObj) {
      fieldObj.x = updatedField.left
      fieldObj.y = updatedField.top
      fieldObj.width = updatedField.width
      fieldObj.height = updatedField.height
      fieldObj.save()
    }
    setFields(prev => ({ ...prev, [updatedField.id]: updatedField }))
  }, [setFields])

  // Handle toolbar updates (font, color, size, etc.)
  const handleToolbarUpdate = useCallback((updates) => {
    if (!selectedId || !fields[selectedId]) return

    const field = fields[selectedId]
    const fieldObj = field.object

    if (fieldObj) {
      if (updates.fontName !== undefined) fieldObj.fontId = updates.fontId
      if (updates.textSize !== undefined) fieldObj.textSize = updates.textSize
      if (updates.color !== undefined) fieldObj.color = updates.color
      if (updates.align !== undefined) fieldObj.align = updates.align
      if (updates.wrap !== undefined) fieldObj.wrap = updates.wrap
      if (updates.spacing !== undefined) fieldObj.spacing = updates.spacing
      if (updates.autoFit !== undefined) fieldObj.autoFit = updates.autoFit
      if (updates.minFontSize !== undefined) fieldObj.minFontSize = updates.minFontSize
      if (updates.fontStyle !== undefined) fieldObj.fontStyle = updates.fontStyle
      if (updates.textDecoration !== undefined) fieldObj.textDecoration = updates.textDecoration
      if (updates.customerEditable !== undefined) fieldObj.customerEditable = updates.customerEditable
      if (updates.sampleText !== undefined) fieldObj.sampleText = updates.sampleText
      fieldObj.save()
    }

    setFields(prev => ({ ...prev, [selectedId]: { ...prev[selectedId], ...updates } }))
  }, [selectedId, fields, setFields])

  // Handle copy settings - stores formatting for paste
  const handleCopySettings = useCallback((field) => {
    // Copy formatting and size properties (not content or position)
    const formatting = {
      width: field.width,
      height: field.height,
      textSize: field.textSize,
      color: field.color,
      fontName: field.fontName,
      fontId: field.fontId || field.object?.fontId,
      fontStyle: field.fontStyle,
      textDecoration: field.textDecoration,
      align: field.align,
      spacing: field.spacing,
      wrap: field.wrap,
      autoFit: field.autoFit,
      minFontSize: field.minFontSize,
    }
    setCopiedFormatting(formatting)
    showToast('Settings copied! Select another field and click the green paste button')
  }, [showToast])

  // Handle paste settings - applies copied formatting to selected field
  const handlePasteSettings = useCallback(() => {
    if (!copiedFormatting || !selectedId) return

    const field = fields[selectedId]
    if (!field || isImageField(field)) {
      showToast('Cannot paste formatting to image fields')
      return
    }

    // Apply formatting to the field
    const updates = { ...copiedFormatting }

    // If field has a database object, update it too
    if (field.object) {
      const fieldObj = field.object
      if (updates.width !== undefined) fieldObj.width = updates.width
      if (updates.height !== undefined) fieldObj.height = updates.height
      if (updates.textSize !== undefined) fieldObj.textSize = updates.textSize
      if (updates.color !== undefined) fieldObj.color = updates.color
      if (updates.fontId !== undefined) fieldObj.fontId = updates.fontId
      if (updates.fontStyle !== undefined) fieldObj.fontStyle = updates.fontStyle
      if (updates.textDecoration !== undefined) fieldObj.textDecoration = updates.textDecoration
      if (updates.align !== undefined) fieldObj.align = updates.align
      if (updates.spacing !== undefined) fieldObj.spacing = updates.spacing
      if (updates.wrap !== undefined) fieldObj.wrap = updates.wrap
      if (updates.autoFit !== undefined) fieldObj.autoFit = updates.autoFit
      if (updates.minFontSize !== undefined) fieldObj.minFontSize = updates.minFontSize
      fieldObj.save()
    }

    setFields(prev => ({ ...prev, [selectedId]: { ...prev[selectedId], ...updates } }))
    showToast('Formatting applied')
  }, [copiedFormatting, selectedId, fields, setFields, fonts, showToast])

  // Handle selection
  const handleSelect = useCallback((fieldId) => {
    setSelectedId(fieldId)
    if (onSelectField) onSelectField(fieldId)
  }, [onSelectField])

  // Handle deselection when clicking on empty area
  const handleStageClick = useCallback((e) => {
    if (e.target === e.target.getStage() || e.target.getClassName() === 'Image') {
      setSelectedId(null)
      if (onSelectField) onSelectField(null)
    }
  }, [onSelectField])

  // Handle right-click on empty canvas area
  const handleStageContextMenu = useCallback((e) => {
    if (e.target !== e.target.getStage()) return

    e.evt.preventDefault()
    const stage = stageRef.current
    const pointerPos = stage.getPointerPosition()
    const wrapperRect = canvasWrapperRef.current.getBoundingClientRect()

    setContextMenu({
      visible: true,
      x: wrapperRect.left + (pointerPos.x * displayScale),
      y: wrapperRect.top + (pointerPos.y * displayScale),
      fieldId: null,
      pastePosition: pointerPos
    })
    setSelectedId(null)
  }, [displayScale])

  // Handle double-click to edit text
  const handleDoubleClick = useCallback((fieldId) => {
    const field = fields[fieldId]
    if (!field || isImageField(field)) return

    setEditingField(fieldId)
    setEditText(field.sampleText || field.text || '')

    setTimeout(() => {
      if (textInputRef.current) {
        textInputRef.current.focus()
        textInputRef.current.select()
      }
    }, 0)
  }, [fields])

  // Handle text edit save
  const handleTextEditSave = useCallback(() => {
    if (!editingField) return

    const currentText = editTextRef.current
    const field = fields[editingField]

    if (field) {
      if (field.object) {
        // Admin mode - save to database
        if (field.columnType === 'static_text') {
          field.object.text = currentText
        } else {
          field.object.sampleText = currentText
        }
        field.object.save()
        setFields(prev => ({
          ...prev,
          [editingField]: {
            ...prev[editingField],
            ...(field.columnType === 'static_text' ? { text: currentText } : { sampleText: currentText })
          }
        }))
      } else {
        // Customer mode - update local state
        setFields(prev => ({
          ...prev,
          [editingField]: { ...prev[editingField], text: currentText, sampleText: null }
        }))
      }
    }

    setEditingField(null)
    setEditText('')
  }, [editingField, fields, setFields])

  // Handle text edit cancel
  const handleTextEditCancel = useCallback(() => {
    setEditingField(null)
    setEditText('')
  }, [])

  // Handle right-click context menu on field
  const handleContextMenu = useCallback((e, fieldId) => {
    e.evt.preventDefault()
    const stage = stageRef.current
    const pointerPos = stage.getPointerPosition()
    const wrapperRect = canvasWrapperRef.current.getBoundingClientRect()

    setContextMenu({
      visible: true,
      x: wrapperRect.left + (pointerPos.x * displayScale),
      y: wrapperRect.top + (pointerPos.y * displayScale),
      fieldId: fieldId,
      pastePosition: null
    })
    setSelectedId(fieldId)
  }, [displayScale])

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu({ visible: false, x: 0, y: 0, fieldId: null, pastePosition: null })
  }, [])

  // Handle copy field
  const handleCopyField = useCallback(() => {
    if (contextMenu.fieldId && fields[contextMenu.fieldId]) {
      setCopiedField({ ...fields[contextMenu.fieldId] })
      showToast('Copied!')
    }
    closeContextMenu()
  }, [contextMenu.fieldId, fields, closeContextMenu, showToast])

  // Handle paste field
  const handlePasteField = useCallback(async () => {
    const pasteX = contextMenu.pastePosition
      ? Math.round(contextMenu.pastePosition.x)
      : copiedField ? (copiedField.left || 0) + 20 : 100
    const pasteY = contextMenu.pastePosition
      ? Math.round(contextMenu.pastePosition.y)
      : copiedField ? (copiedField.top || 0) + 20 : 100

    // If no copied field, try to create from system clipboard
    if (!copiedField) {
      try {
        const clipboardText = await navigator.clipboard.readText()
        if (clipboardText && clipboardText.trim()) {
          const newField = createTextField(clipboardText.trim(), pasteX, pasteY)
          setFields(prev => ({ ...prev, [newField.id]: newField }))
          setSelectedId(newField.id)
          showToast('Text pasted')
          closeContextMenu()
          return
        }
      } catch (err) {
        console.log('No clipboard text available')
      }
      closeContextMenu()
      return
    }

    // Customer mode: create local-only field
    if (!copiedField.object) {
      const newField = createTextField(
        copiedField.text || copiedField.sampleText || '',
        pasteX,
        pasteY,
        {
          width: copiedField.width,
          height: copiedField.height,
          textSize: copiedField.textSize,
          color: copiedField.color,
          align: copiedField.align,
          spacing: copiedField.spacing,
          wrap: copiedField.wrap,
          fontName: copiedField.fontName,
          fontStyle: copiedField.fontStyle,
          textDecoration: copiedField.textDecoration,
          autoFit: copiedField.autoFit,
          minFontSize: copiedField.minFontSize,
          label: copiedField.label ? `${copiedField.label} (copy)` : 'Custom Text',
        }
      )
      setFields(prev => ({ ...prev, [newField.id]: newField }))
      setSelectedId(newField.id)
      showToast('Pasted')
      closeContextMenu()
      return
    }

    // Admin mode: create database-backed field
    const sourceField = copiedField.object
    const Field = (await import('../../../models/Field')).default

    const newField = new Field({
      x: pasteX,
      y: pasteY,
      width: copiedField.width,
      height: copiedField.height,
      align: copiedField.align,
      spacing: copiedField.spacing,
      color: copiedField.color,
      fontId: copiedField.fontId,
      textSize: copiedField.textSize,
      text: copiedField.text,
      sampleText: copiedField.sampleText,
      wrap: copiedField.wrap,
      autoFit: copiedField.autoFit,
      minFontSize: copiedField.minFontSize,
      fontStyle: copiedField.fontStyle,
      textDecoration: copiedField.textDecoration,
      templateId: template.id,
      columnId: sourceField.columnId
    })

    newField.save().then(() => {
      Field.includes(['column', 'font'])
        .find(newField.id)
        .then((res) => {
          setFields(prev => ({
            ...prev,
            [newField.id]: {
              ...copiedField,
              id: newField.id,
              left: pasteX,
              top: pasteY,
              object: res.data
            }
          }))
          setSelectedId(newField.id)
          showToast('Pasted')
        })
    })

    closeContextMenu()
  }, [copiedField, contextMenu.pastePosition, template.id, setFields, closeContextMenu, showToast])

  // Handle delete field
  const handleDeleteField = useCallback(() => {
    if (!contextMenu.fieldId || !fields[contextMenu.fieldId]) {
      closeContextMenu()
      return
    }

    const field = fields[contextMenu.fieldId]
    if (field.object) {
      field.object.destroy()
    }

    setFields(prev => {
      const updated = { ...prev }
      delete updated[contextMenu.fieldId]
      return updated
    })
    setSelectedId(null)
    showToast('Deleted')
    closeContextMenu()
  }, [contextMenu.fieldId, fields, setFields, closeContextMenu, showToast])

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) closeContextMenu()
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [contextMenu.visible, closeContextMenu])

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = async (e) => {
      if (editingField) return
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

      // Undo: Cmd+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        handleUndo()
        return
      }

      // Redo: Cmd+Shift+Z or Cmd+Y
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        handleRedo()
        return
      }

      // Paste: Cmd+V
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault()

        // If a text field is selected, paste text into it
        if (selectedId && fields[selectedId] && !isImageField(fields[selectedId])) {
          try {
            const clipboardText = await navigator.clipboard.readText()
            if (clipboardText) {
              const field = fields[selectedId]
              if (field.object) {
                if (field.columnType === 'static_text') {
                  field.object.text = clipboardText
                } else {
                  field.object.sampleText = clipboardText
                }
                field.object.save()
                setFields(prev => ({
                  ...prev,
                  [selectedId]: {
                    ...prev[selectedId],
                    ...(field.columnType === 'static_text' ? { text: clipboardText } : { sampleText: clipboardText })
                  }
                }))
              } else {
                setFields(prev => ({
                  ...prev,
                  [selectedId]: { ...prev[selectedId], text: clipboardText, sampleText: null }
                }))
              }
              showToast('Text pasted')
              return
            }
          } catch (err) {
            console.log('Clipboard access not available')
          }
        }

        // No field selected - create new text element from clipboard
        try {
          const clipboardText = await navigator.clipboard.readText()
          if (clipboardText && clipboardText.trim()) {
            const newField = createTextField(clipboardText.trim(), 100, 100)
            setFields(prev => ({ ...prev, [newField.id]: newField }))
            setSelectedId(newField.id)
            showToast('Text pasted')
          }
        } catch (err) {
          console.log('Clipboard access not available')
        }
        return
      }

      // Below shortcuts require a selected field
      if (!selectedId || !fields[selectedId]) return
      const field = fields[selectedId]

      // Copy: Cmd+C
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        setCopiedField({ ...field })
        showToast('Copied!')
        return
      }

      // Arrow keys - nudge element
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault()
        const nudgeAmount = e.shiftKey ? 10 : 1
        let deltaX = 0, deltaY = 0

        switch (e.key) {
          case 'ArrowUp': deltaY = -nudgeAmount; break
          case 'ArrowDown': deltaY = nudgeAmount; break
          case 'ArrowLeft': deltaX = -nudgeAmount; break
          case 'ArrowRight': deltaX = nudgeAmount; break
        }

        const updatedField = {
          ...field,
          left: (field.left || 0) + deltaX,
          top: (field.top || 0) + deltaY,
        }

        if (field.object) {
          field.object.x = updatedField.left
          field.object.y = updatedField.top
          field.object.save()
        }

        setFields(prev => ({ ...prev, [selectedId]: updatedField }))
        return
      }

      // Delete/Backspace - delete selected field
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (field.object) {
          field.object.destroy()
        }
        setFields(prev => {
          const updated = { ...prev }
          delete updated[selectedId]
          return updated
        })
        setSelectedId(null)
        showToast('Deleted')
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => document.removeEventListener('keydown', handleGlobalKeyDown)
  }, [editingField, selectedId, fields, setFields, handleUndo, handleRedo, showToast])

  // Calculate scaled dimensions for layout
  const scaledWidth = Math.round(dimensions.width * displayScale)
  const scaledHeight = Math.round(dimensions.height * displayScale)

  return (
    <div ref={containerRef} className={styles.canvasContainer}>
      {/* Layout wrapper */}
      <div
        className={styles.layoutWrapper}
        style={{ width: scaledWidth, height: scaledHeight, position: 'relative' }}
      >
        {/* Canvas wrapper with transform scale */}
        <div
          ref={canvasWrapperRef}
          className={styles.canvasWrapper}
          style={{
            width: dimensions.width,
            height: dimensions.height,
            transform: `scale(${displayScale})`,
            transformOrigin: 'top left',
          }}
        >
          <img
            src={imageUrl}
            alt="Template background"
            className={styles.backgroundImage}
            style={{ width: dimensions.width, height: dimensions.height }}
          />
          <Stage
            ref={stageRef}
            width={dimensions.width}
            height={dimensions.height}
            onClick={handleStageClick}
            onTap={handleStageClick}
            onContextMenu={handleStageContextMenu}
            className={styles.konvaStage}
          >
            <Layer>
              {Object.keys(fields).map((key) => {
                const field = fields[key]
                if (isImageField(field)) {
                  return (
                    <TransformableImage
                      key={key}
                      field={field}
                      isSelected={selectedId === key}
                      onSelect={() => handleSelect(key)}
                      onChange={handleFieldChange}
                      onContextMenu={(e) => handleContextMenu(e, key)}
                      hideBoundingBoxes={hideBoundingBoxes}
                    />
                  )
                }
                return (
                  <TransformableText
                    key={key}
                    field={field}
                    isSelected={selectedId === key}
                    onSelect={() => handleSelect(key)}
                    onChange={handleFieldChange}
                    onDoubleClick={() => handleDoubleClick(key)}
                    onContextMenu={(e) => handleContextMenu(e, key)}
                    hideBoundingBoxes={hideBoundingBoxes}
                  />
                )
              })}
            </Layer>
          </Stage>

          {/* Inline text editing */}
          {editingField && fields[editingField] && (
            <textarea
              ref={textInputRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleTextEditCancel()
                else if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleTextEditSave()
                }
              }}
              onBlur={handleTextEditSave}
              className={styles.inlineTextEdit}
              style={{
                position: 'absolute',
                left: fields[editingField].left || 0,
                top: fields[editingField].top || 0,
                width: fields[editingField].width || 100,
                minHeight: fields[editingField].height || 30,
                fontSize: fields[editingField].textSize || 16,
                fontFamily: fields[editingField].fontName || 'Arial',
                color: fields[editingField].color || '#000000',
                textAlign: fields[editingField].align || 'left',
                zIndex: 100,
              }}
            />
          )}
        </div>
      </div>

      {/* Floating toolbar */}
      {selectedField && !isImageField(selectedField) && !contextMenu.visible && (
        <FloatingToolbar
          field={selectedField}
          onUpdate={handleToolbarUpdate}
          onCopySettings={handleCopySettings}
          onPasteSettings={handlePasteSettings}
          hasCopiedFormatting={!!copiedFormatting}
          position={toolbarPosition}
          fonts={fonts}
        />
      )}

      {/* Context menu */}
      {contextMenu.visible && (
        <div
          className={styles.contextMenu}
          style={{
            left: Math.min(contextMenu.x, window.innerWidth - 160),
            top: Math.min(contextMenu.y, window.innerHeight - 120)
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.fieldId ? (
            <>
              <button onClick={handleCopyField}><span>📋</span> Copy</button>
              <button onClick={handlePasteField}><span>📄</span> Paste</button>
              <hr />
              <button onClick={handleDeleteField} className={styles.deleteBtn}>
                <span>🗑️</span> Delete
              </button>
            </>
          ) : (
            <button onClick={handlePasteField}><span>📄</span> Paste</button>
          )}
        </div>
      )}

      {/* Toast notification */}
      {toast.visible && (
        <div className={styles.toast}>{toast.message}</div>
      )}

      {showControls && (
        <>
          <div className={styles.controls}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={hideBoundingBoxes}
                onChange={(e) => setHideBoundingBoxes(e.target.checked)}
              />
              <span>Hide bounding boxes</span>
            </label>
          </div>
          <p className={styles.helpText}>
            <small>Click to select, drag to move, arrows to nudge. Double-click text to edit. Cmd+Z to undo.</small>
          </p>
        </>
      )}
    </div>
  )
}
